// Auth context — Supabase implementation.
//
// Same shape as the Firebase version so no consumer changes; `firebaseUser`
// keeps its name (now a Supabase User) rather than churning every call site.
//
// The role comes from the `user_role` claim in the JWT, stamped by the
// custom_access_token_hook. Two consequences worth knowing:
//
//   * We never query `profiles` to decide permissions. RLS reads the same
//     claim, so the client and the database always agree — with a profile
//     lookup they could drift.
//
//   * A role change lands on the next token refresh. `refresh()` forces one,
//     so promoting a cashier takes effect without them logging out.
//
// A deactivated user gets NO claim at all (the hook omits it), so is_staff()
// fails server-side and every query returns empty. We detect that here and
// sign them out with a clear message rather than showing empty screens.

import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import {
  supabase,
  isSupabaseConfigured,
  roleFromSession,
} from '@/lib/supabase';
import type { AppUser } from '@/types/domain';
import { claimsFromSession, EMPTY_CLAIMS, type SessionClaims } from '@/lib/claims';

interface AuthState {
  user: AppUser | null;
  /**
   * Everything the token says this session may do. Kept beside `user` rather
   * than folded into it because it is not a property of the person — it is a
   * property of the token they are holding, and it changes on refresh without
   * the person changing at all.
   */
  claims: SessionClaims;
  /** The Supabase auth user. Named for continuity with the Firebase version. */
  firebaseUser: User | null;
  loading: boolean;
  error: string | null;
  configValid: boolean;
}

interface AuthContextValue extends AuthState {
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    claims: EMPTY_CLAIMS,
    firebaseUser: null,
    loading: true,
    error: null,
    configValid: isSupabaseConfigured,
  });

  // A token minted before the roles migration carries `user_role` but no
  // `role_id`/`perms`, and the sidebar and route gates read those. Refresh such
  // a token once so the new claims arrive without a re-login; remembering
  // which token was refreshed keeps this from looping if the hook still
  // stamps nothing (a deactivated account — handled just below).
  const refreshedToken = useRef<string | null>(null);

  const hydrate = useCallback(async (session: Session | null) => {
    if (!session?.user) {
      setState((s) => ({
        ...s,
        user: null,
        claims: EMPTY_CLAIMS,
        firebaseUser: null,
        loading: false,
      }));
      return;
    }

    const role = roleFromSession(session);
    if (!role) {
      // No claim means the profile is missing or deactivated. Staying signed
      // in would just render empty screens, since RLS denies everything.
      await supabase.auth.signOut();
      setState((s) => ({
        ...s,
        user: null,
        claims: EMPTY_CLAIMS,
        firebaseUser: null,
        loading: false,
        error: 'Akun ini tidak aktif. Hubungi owner.',
      }));
      return;
    }

    const claims = claimsFromSession(session);
    if (!claims.roleId && refreshedToken.current !== session.access_token) {
      refreshedToken.current = session.access_token;
      const { data } = await supabase.auth.refreshSession();
      // onAuthStateChange hydrates the refreshed session. Only fall through
      // (and show the legacy view) when the refresh produced nothing new.
      if (data.session && data.session.access_token !== session.access_token) return;
    }

    const { data: profile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', session.user.id)
      .maybeSingle();

    setState({
      claims,
      user: {
        uid: session.user.id,
        email: profile?.email ?? session.user.email ?? '',
        displayName:
          profile?.display_name || session.user.email?.split('@')[0] || 'User',
        // Trust the token, not the row: the token is what RLS enforces.
        role,
        activeEventId: profile?.active_event_id ?? undefined,
        createdAt: profile?.created_at ? new Date(profile.created_at).getTime() : Date.now(),
      },
      firebaseUser: session.user,
      loading: false,
      error: null,
      configValid: isSupabaseConfigured,
    });
  }, []);

  useEffect(() => {
    let active = true;

    supabase.auth.getSession().then(({ data }) => {
      if (active) void hydrate(data.session);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (active) void hydrate(session);
    });

    /**
     * Re-check the session whenever the tab comes back to the foreground.
     *
     * Access tokens last an hour here (GOTRUE_JWT_EXP=3600). A tab left open
     * overnight wakes holding a dead one, and supabase-js only discovers that
     * on its next refresh attempt — so until something fails, the app looks
     * signed in and every call returns 401. That is exactly how a "Failed to
     * load resource: 401" reaches someone who has done nothing wrong.
     *
     * getSession() refreshes when it can and returns null when it cannot, and
     * hydrate(null) sends them to the login screen with the reason obvious.
     */
    const recheck = () => {
      if (document.visibilityState !== 'visible') return;
      void supabase.auth.getSession().then(({ data }) => {
        if (active) void hydrate(data.session);
      });
    };
    document.addEventListener('visibilitychange', recheck);
    window.addEventListener('focus', recheck);

    return () => {
      active = false;
      sub.subscription.unsubscribe();
      document.removeEventListener('visibilitychange', recheck);
      window.removeEventListener('focus', recheck);
    };
  }, [hydrate]);

  const signIn = useCallback(async (email: string, password: string) => {
    setState((s) => ({ ...s, loading: true, error: null }));
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    if (error) {
      const message = mapAuthError(error.message);
      setState((s) => ({ ...s, loading: false, error: message }));
      throw new Error(message);
    }
    // onAuthStateChange hydrates.
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setState((s) => ({
      ...s,
      user: null,
      claims: EMPTY_CLAIMS,
      firebaseUser: null,
      error: null,
    }));
  }, []);

  /** Force a token refresh so a role change applies without re-login. */
  const refresh = useCallback(async () => {
    const { data } = await supabase.auth.refreshSession();
    await hydrate(data.session);
  }, [hydrate]);

  return (
    <AuthContext.Provider value={{ ...state, signIn, signOut, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

function mapAuthError(message: string): string {
  const m = message.toLowerCase();
  if (m.includes('invalid login credentials')) return 'Email atau password salah';
  if (m.includes('email not confirmed')) return 'Email belum dikonfirmasi';
  if (m.includes('rate limit') || m.includes('too many')) {
    return 'Terlalu banyak percobaan. Coba lagi nanti';
  }
  if (m.includes('fetch') || m.includes('network')) {
    return 'Koneksi gagal. Periksa internet kamu';
  }
  return 'Gagal masuk. Coba lagi';
}
